// src/riskmonitors/ScenarioReport.jsx
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectRiskEntries, selectAllControls } from './redux/selectors/assessmentSelectors';
import { selectScenarioMonitor } from './redux/selectors/monitorSelectors';
import {
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export default function NarrativeScenarioReport({ modelId, onClose }) {
  // Pull scenario monitor state from its own slice
  const { data: monitorData } = useSelector(state => selectScenarioMonitor(state, modelId));
  const report = monitorData?.report ?? {};
  const { updateRisks = [], newRisks = [], updateCtrls = [], newCtrls = [] } = report;

  // Get entries and allCtrls at the top level
  const entries = useSelector(state => selectRiskEntries(state, modelId));
  const allCtrls = useSelector(state => selectAllControls(state));

  // entriesMap: riskId -> entry
  const entriesMap = useMemo(() => {
    const m = {};
    (entries || []).forEach(e => { m[e.riskId] = e; });
    return m;
  }, [entries]);

  // newCtrlsByRisk: risk title -> [controls]
  const newCtrlsByRisk = useMemo(() => {
    const m = {};
    (newCtrls || []).forEach(c => {
      const key = c['new risk title'];
      if (!m[key]) m[key] = [];
      m[key].push(c);
    });
    return m;
  }, [newCtrls]);

  // updCtrlsByRisk: riskId -> [controls]
  const updCtrlsByRisk = useMemo(() => {
    const m = {};
    (updateCtrls || []).forEach(c => {
      const control = allCtrls[c.controlId];
      if (!control) return;
      const rid = control.riskId;
      if (!m[rid]) m[rid] = [];
      m[rid].push({ ...c, name: control.name });
    });
    return m;
  }, [updateCtrls, allCtrls]);

  return (
    <Box p={2}>
      <Button sx={{ mb: 2 }} onClick={onClose}>← Back</Button>
      <Typography variant="h6" gutterBottom>
        Scenario Revision Report
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* New Risks */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            New Risks ({newRisks.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {newRisks.length === 0 && (
            <Typography color="text.secondary">No new risks identified.</Typography>
          )}
          {newRisks.map((r, idx) => {
            const ctrls = newCtrlsByRisk[r.title] || [];
            return (
              <Paper key={idx} elevation={0} sx={{ mb: 3, p: 2 }}>
                <Typography variant="subtitle2">
                  {r.title}{' '}
                  <Tooltip title={r.reason} arrow>
                    <InfoOutlinedIcon fontSize="inherit" sx={{ verticalAlign:'text-top' }}/>
                  </Tooltip>
                </Typography>
                {ctrls.length > 0 ? (
                  <TableContainer sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Suggestion</TableCell>
                          <TableCell>Info</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ctrls.map((c,i) => (
                          <TableRow key={i}>
                            <TableCell>{c.name}</TableCell>
                            <TableCell>{c.suggestion}</TableCell>
                            <TableCell>
                              <Tooltip title={c.reason} arrow>
                                <InfoOutlinedIcon fontSize="small"/>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    No recommended actions for this risk.
                  </Typography>
                )}
              </Paper>
            );
          })}
        </AccordionDetails>
      </Accordion>

      {/* Updated Risks */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            Updated Risks ({updateRisks.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {updateRisks.length === 0 && (
            <Typography color="text.secondary">No updated risks.</Typography>
          )}
          {updateRisks.map(ur => {
            const entry = entriesMap[ur.riskId] || {};
            const ctrls = updCtrlsByRisk[ur.riskId] || [];
            return (
              <Paper key={ur.riskId} elevation={0} sx={{ mb: 3, p: 2 }}>
                <Typography variant="subtitle2">
                  {entry.title || '(unknown risk)'}{' '}
                  <Tooltip title={ur.reason} arrow>
                    <InfoOutlinedIcon fontSize="inherit" sx={{ verticalAlign:'text-top' }}/>
                  </Tooltip>
                </Typography>
                {ctrls.length > 0 ? (
                  <TableContainer sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Suggestion</TableCell>
                          <TableCell>Info</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ctrls.map(c => (
                          <TableRow key={c.controlId}>
                            <TableCell>{c.name}</TableCell>
                            <TableCell>{c.suggestion}</TableCell>
                            <TableCell>
                              <Tooltip title={c.reason} arrow>
                                <InfoOutlinedIcon fontSize="small"/>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    Risk flagged for control update, but no controls returned.
                  </Typography>
                )}
              </Paper>
            );
          })}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}